import { MESSAGE_TYPES } from "cfg/endpoints";
import { AGENT_TYPE, AIProspectEmail, EmailValueProp, Bot, BotLanguage, BotStyle, BotVoiceType, CallDifficultyMode, CallLanguage } from "interfaces/db";
import { EmailGenRequest } from "interfaces/ai-automations";
import { EMAIL_INPUT } from "interfaces/ai-automations";
import { UserDataResult } from "interfaces/users";
import { Typography } from "interfaces/typography";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { getServicesManager } from "services";
import { ProspectData, jsonSafeToAgentData, AGENT_DATA } from "./interfaces";
import { ProspectOverviewPage } from "./ProspectDataPage";
import { sendMessageToTop } from "lib/user-agent";
import { ChatGPTGenerationPage } from "../ChatGPTGenerationView/ChatGPTGenerationPage";
import { CustomEditInfo } from "interfaces/ai-automations";
import { RootState } from "store";
import { useSelector, useDispatch } from "react-redux";
import { Dispatch, AnyAction } from "@reduxjs/toolkit";
import { reloadEmailValueProps } from 'lib/redux/users/slice';
import { reloadUser } from 'lib/redux/users/slice';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { PracticeBotView } from './PracticeBotView';
import { EXTENSION_ID } from "cfg/endpoints";
import uuid from "react-uuid";
import { RequestState } from "interfaces/ai-automations";
import { useMessageToTop } from "utils/useMessageToTop";
import { useSessionByNumbers } from "./useSessionByNumbers";
import { SessionDetailView } from "components/History/views";
import { SESSION_METRICS_COLUMNS } from "interfaces/table";

export enum PageView {   
    EMAIL_GENERATION_VIEW = 'EMAIL_GENERATION_VIEW',
    PROSPECT_DATA_VIEW = 'PROSPECT_DATA_VIEW',
    PRACTICE_BOT_VIEW = 'PRACTICE_BOT_VIEW'
}

export type EmbeddedAutomatorProps = {
    email_value_props: EmailValueProp[] | null
    dispatch: Dispatch<AnyAction>
    user: UserDataResult | null
 } 

export const EmbeddedAutomator: React.FC = () => {
    const getEmptyProspectData = (): ProspectData => {
        return {
            prospect_name: null,
            prospect_company: null,
            prospect_title: null,
            agent_data: {
                [AGENT_TYPE.LINKEDIN]: {status: RequestState.DEFAULT, url: null, info: undefined, has_data: false, },
                [AGENT_TYPE.COMPANY_WEBSITE]: {status: RequestState.DEFAULT, url: null, info: undefined, has_data: false, },
            },
            numbers: []
        }
    };

    const dispatch = useDispatch();
    const email_value_props = useSelector((state: RootState) => {
        let value_props = state.emailValueProps.value;
        if (value_props) value_props = Array.from(value_props).sort((a, b) => getPriority(a) - getPriority(b));
        return value_props;
    });
    const { user } = useUserData();
    const [inputData, setInputData] = useState<ProspectData>(getEmptyProspectData());
    const [latestProspectId, setLatestProspectId] = useState<string | null>(null);
    const [emailRequestedForProspectId, setEmailRequestedForProspectId] = useState<string | null>(null);
    const [generatedEmail, setGeneratedEmail] = useState<{[k: string]: AIProspectEmail | null}>({});
    const [selectedSection, setSelectedSection] = useState<EMAIL_INPUT>(EMAIL_INPUT.TRANSCRIPTS);
    const [currentView, setCurrentView] = useState<PageView>(PageView.PROSPECT_DATA_VIEW);
    const [conversationalHistories, setConversationalHistories] = useState<{[k: string]: CustomEditInfo[]}>({});
    const [conversationalGeneratedEmails, setConversationalGeneratedEmails] = useState<{[k: string]: {[uuid in string]: AIProspectEmail | null}}>({});
    const [isCreatingBot, setIsCreatingBot] = useState<boolean>(false);
    const [prospectBots, setProspectBots] = useState<{[prospectId: string]: Bot}>({});
    const [prospectBotsSearched, setProspectBotsSearched] = useState<{[prospectId: string]: boolean}>({});
    const [prospectBotsSearching, setProspectBotsSearching] = useState<{[prospectId: string]: boolean}>({});
    const [selectedDifficulty, setSelectedDifficulty] = useState<CallDifficultyMode>(CallDifficultyMode.MEDIUM);
    const [selectedLanguage, setSelectedLanguage] = useState<CallLanguage>(CallLanguage.ENGLISH);

    // Ref to track previous conversational histories for comparison
    const prevConversationalHistoriesRef = useRef<{[k: string]: CustomEditInfo[]}>({});
    
    // Ref to track previous agent data for comparison
    const prevAgentDataRef = useRef<{[k in AGENT_TYPE]: AGENT_DATA}>({
        [AGENT_TYPE.LINKEDIN]: {status: RequestState.DEFAULT, url: null, info: undefined, has_data: false},
        [AGENT_TYPE.COMPANY_WEBSITE]: {status: RequestState.DEFAULT, url: null, info: undefined, has_data: false}
    });

    // Use the message to top hook
    const { sendMessageToTop: _sendMessageToTop, passMessageToWeb } = useMessageToTop();

    // Use the session by numbers hook to get transcript count
    const sessionByNumbersResult = useSessionByNumbers(inputData.numbers, SessionDetailView.EMBEDDED);
    const { count: transcriptCount, isLoading: transcriptLoading, items: sessionItems } = sessionByNumbersResult;

    // Send transcript count when it changes
    useEffect(() => {
        // Send message when transcript count is available (including 0) and not actively loading
        // Only send if there are numbers to search for (matching old version behavior)
        if (inputData.numbers.length > 0 && !transcriptLoading) {
            _sendMessageToTop({
                'type': MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_HAS_TRANSCRIPT, 
                'numbers': inputData.numbers,
                'numberFound': transcriptCount,
            });
            passMessageToWeb('TRANSCRIPT_LOOKUP', {
                type: MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_HAS_TRANSCRIPT,
                numbers: inputData.numbers,
                numberFound: transcriptCount,
            });
        }
    }, [inputData.numbers, transcriptCount, transcriptLoading, sessionItems, _sendMessageToTop, passMessageToWeb]);

    const convertProspectIdForGeneration = useCallback((prospect_id: string | null) => {
        if (!prospect_id) return null;
        if (prospect_id.includes('gong.io')) return `${inputData.prospect_name} ${inputData.prospect_company}`;
        return prospect_id;
    }, [inputData.prospect_name, inputData.prospect_company]);

    const getProspectIdForGeneration = useCallback(() => {
        return convertProspectIdForGeneration(latestProspectId);
    }, [latestProspectId, convertProspectIdForGeneration]);

    const compareNumbersArray = useCallback((a: string[], b: string[]) => {
        return a.length === b.length && JSON.stringify(a.sort()) === JSON.stringify(b.sort());
    }, []);

    const onProspectIdentifierReceived = useCallback((prospect_identifier: string | null) => {
        setLatestProspectId(prospect_identifier);
        
        // Clear all input data when prospect identifier is cleared
        if (!prospect_identifier) {
            setInputData(getEmptyProspectData());
        }
    }, []);

    const onAgentInfoUpdate = useCallback((agent_type: AGENT_TYPE, data: any) => {
        const agent_data = jsonSafeToAgentData(data);
        setInputData((prevInputData) => {
            if (prevInputData.agent_data[agent_type].url === agent_data.url && prevInputData.agent_data[agent_type].has_data) return prevInputData;
            return { 
                ...prevInputData, 
                agent_data: {
                    ...prevInputData.agent_data, 
                    [agent_type]: agent_data
                }
            };
        });
    }, []);

    const onNumberInfoUpdate = useCallback((data: any) => {
        const numbers = data.numbers || [];
        if (compareNumbersArray(numbers, inputData.numbers)) return;
        setInputData(prevInputData => ({
            ...prevInputData,
            numbers: numbers
        }));
    }, [compareNumbersArray, inputData.numbers]);

    const onProspectMetadataUpdate = useCallback((prospect_value: string, key: 'prospect_name' | 'prospect_company' | 'prospect_title') => {
        setInputData((prevInputData) => {
            return { 
                ...prevInputData, 
                [key]: prospect_value
            };
        });
    }, []);

    const onInputReceived = useCallback((input: EMAIL_INPUT, data: any, prospect_identifier: string) => {
        onProspectIdentifierReceived(prospect_identifier);
        switch(input) {
            case EMAIL_INPUT.LINKEDIN: onAgentInfoUpdate(AGENT_TYPE.LINKEDIN, data); break;
            case EMAIL_INPUT.WEBSITE: onAgentInfoUpdate(AGENT_TYPE.COMPANY_WEBSITE, data); break;
            case EMAIL_INPUT.TRANSCRIPTS: onNumberInfoUpdate(data); break;
            case EMAIL_INPUT.PROSPECT_NAME: onProspectMetadataUpdate(data, 'prospect_name'); break;
            case EMAIL_INPUT.PROSPECT_COMPANY: onProspectMetadataUpdate(data, 'prospect_company'); break;
            case EMAIL_INPUT.PROSPECT_TITLE: onProspectMetadataUpdate(data, 'prospect_title'); break;
        }
    }, [onProspectIdentifierReceived, onAgentInfoUpdate, onNumberInfoUpdate, onProspectMetadataUpdate]);

    const onWindowMessage = useCallback((event: MessageEvent) => {
        const data = event.data;
        if (!data['type']) return;
        switch (data['type']) {
            case MESSAGE_TYPES.APP_TO_EXTERNAL_CHECK_IS_LOADED:
                _sendMessageToTop({'type': MESSAGE_TYPES.EXTERNAL_TO_APP_IS_LOADED});
                break;
            case MESSAGE_TYPES.PRE_CALL_TO_EXTERNAL_IS_LOADED:
                _sendMessageToTop({'type': MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_IS_LOADED});
                passMessageToWeb('DATA_IFRAME', {'type': MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_IS_LOADED});
                break;
            case MESSAGE_TYPES.PRE_CALL_TO_EXTERNAL_UPDATE_EMAIL_INPUT:
                onInputReceived(data['input'], data['data'], data['id']);
                break;
            case MESSAGE_TYPES.PRE_CALL_TO_EXTERNAL_SCRAPED_DATA:
                const agent_type = data['agent_type'] as AGENT_TYPE;
                setInputData((prevInputData) => {
                    if (prevInputData.agent_data[agent_type].url !== data['url']) return prevInputData;
                    return { 
                        ...prevInputData, 
                        agent_data: {
                            ...prevInputData.agent_data, 
                            [agent_type]: {
                                ...prevInputData.agent_data[agent_type], 
                                scraped_data: data['scraped_data'],
                                status: data['scraped_data'] ? RequestState.REQUESTED_BY_USER : RequestState.ERROR,
                                error_reason: data['error_reason']
                            }
                        }
                    };
                });
                break;
            case MESSAGE_TYPES.PRE_CALL_TO_EXTERNAL_REQUEST_EMAIL:
                if (!getProspectIdForGeneration()) return;
                setCurrentView(PageView.EMAIL_GENERATION_VIEW);
                setEmailRequestedForProspectId(getProspectIdForGeneration());
                break;
        }
    }, [_sendMessageToTop, passMessageToWeb, onInputReceived, getProspectIdForGeneration]);

    const generateEmail = useCallback(async () => {
        const generated_for_prospect_id = getProspectIdForGeneration();
        if (!email_value_props || !generated_for_prospect_id) return;
        if (generatedEmail[generated_for_prospect_id]) return;

        // Process session items to extract session IDs similar to EmbeddedOld.tsx logic
        const previous_session_ids = sessionItems.map(session => session.session_id);
        const selected_session_ids = sessionItems
            .filter(session => {
                const targetDuration = session.result.get(SESSION_METRICS_COLUMNS.TARGET_DURATION);
                const gatekeeperDuration = session.result.get(SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION);
                return (targetDuration && targetDuration > 0) || (gatekeeperDuration && gatekeeperDuration > 0);
            })
            .map(session => session.session_id);

        const response = await getServicesManager().generateProspectEmail({
            prospect_id: generated_for_prospect_id,
            previous_session_ids: previous_session_ids,
            selected_session_ids: selected_session_ids,
            value_prop: email_value_props[0],
            prospect_info: {
                'prospect_name': inputData.prospect_name,
                'prospect_company': inputData.prospect_company,
                'prospect_title': inputData.prospect_title,
                'website_summary': inputData.agent_data.COMPANY_WEBSITE.info?.summary ?? null,
                'linkedin_summary': inputData.agent_data.LINKEDIN.info?.summary ?? null,
            }
        });
        const email = response?.gen_email;
        if (!email) return;
        setGeneratedEmail((prevGeneratedEmail) => {
            return {
                ...prevGeneratedEmail, 
                [generated_for_prospect_id]: response
            };
        });
    }, [getProspectIdForGeneration, email_value_props, generatedEmail, inputData, sessionItems]);

    const generateRevisionEmail = useCallback(async (previous_email: string, custom_edit_info: CustomEditInfo) => {
        const generated_for_prospect_id = getProspectIdForGeneration();
        if (!email_value_props || !generated_for_prospect_id) return;

        // Process session items to extract session IDs similar to EmbeddedOld.tsx logic
        const previous_session_ids = sessionItems.map(session => session.session_id);
        const selected_session_ids = sessionItems
            .filter(session => {
                const targetDuration = session.result.get(SESSION_METRICS_COLUMNS.TARGET_DURATION);
                const gatekeeperDuration = session.result.get(SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION);
                return (targetDuration && targetDuration > 0) || (gatekeeperDuration && gatekeeperDuration > 0);
            })
            .map(session => session.session_id);

        const emailRequest: EmailGenRequest = {
            prospect_id: generated_for_prospect_id,
            value_prop: email_value_props[0],
            prospect_info: {
                'prospect_name': inputData.prospect_name,
                'prospect_company': inputData.prospect_company,
                'prospect_title': inputData.prospect_title,
                'website_summary': inputData.agent_data.COMPANY_WEBSITE.info?.summary ?? null,
                'linkedin_summary': inputData.agent_data.LINKEDIN.info?.summary ?? null,
            },
            previous_session_ids: previous_session_ids,
            selected_session_ids: selected_session_ids,
        };
        const response = await getServicesManager().generateRevisionEmail(emailRequest, previous_email, custom_edit_info);
        setConversationalGeneratedEmails((prevConversationalGeneratedEmails) => {
            const generated_emails = prevConversationalGeneratedEmails[generated_for_prospect_id] ?? {};
            return {
                ...prevConversationalGeneratedEmails,
                    [generated_for_prospect_id]: {
                        ...generated_emails,
                        [custom_edit_info.uuid]: response
                    }
            };
        });
    }, [getProspectIdForGeneration, email_value_props, inputData, sessionItems]);

    const getScrapedData = useCallback(async (url: string, scraped_data: string | null, checkCache: boolean, agent_type: AGENT_TYPE) => {
        const info = await getServicesManager().getScrapedSummarizedData(url, scraped_data, checkCache, agent_type);
        const status = info?.summary || info?.custom_json ? RequestState.RECEIVED : agent_type === AGENT_TYPE.COMPANY_WEBSITE ? RequestState.ERROR : RequestState.TO_BE_REQUESTED_BY_USER;
        const has_data = info?.summary != null || info?.custom_json != null;
        sendMessageToTop({
            'type': MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_HAS_DATA_ALREADY, 
            'agent_type': agent_type, 
            'has_data': has_data,
            'url': url,
            'status': status
        });
        passMessageToWeb('RESEARCH_LOOKUP', {
            type: MESSAGE_TYPES.EXTERNAL_TO_PRE_CALL_HAS_DATA_ALREADY,
            agent_type: agent_type,
            has_data: has_data,
            url: url,
            status: status
        });
        setInputData((prevInputData) => {
            if (prevInputData.agent_data[agent_type].url !== url) return prevInputData;
            return { 
                ...prevInputData, 
                agent_data: {
                    ...prevInputData.agent_data, 
                    [agent_type]: {
                        url: url, 
                        status: status, 
                        info: info, 
                        has_data: has_data
                    }
                }
            };
        });
    }, [passMessageToWeb]);

    const _compareConversationHistories = useCallback((historyA: CustomEditInfo[], historyB: CustomEditInfo[]): boolean => {
        if (historyA.length !== historyB.length) return false;
        if (historyA.map((v) => v.uuid).sort().join('') !== historyB.map((v) => v.uuid).sort().join('')) return false;
        return true;
    }, []);

    const _hasAnyData = useCallback(() => {
        return inputData.numbers.length > 0 || inputData.agent_data.COMPANY_WEBSITE.url || inputData.agent_data.LINKEDIN.url;
    }, [inputData]);

    const _canRequestConversationalHistory = useCallback((original_email: AIProspectEmail | null, 
        conversational_history: CustomEditInfo[], 
        generated_emails: {[uuid in string]: AIProspectEmail | null}) => {
        if (!original_email) return false;
        if (conversational_history.some((v) => !(v.uuid in generated_emails))) return false;
        if (conversational_history.length >= 3) return false; // cannot request more than 3 revisions
        return true;
    }, []);

    const _onRequestConversationHistoryUpdate = useCallback((conversational_edit: CustomEditInfo) => {
        const generated_for_prospect_id = getProspectIdForGeneration();
        if (!generated_for_prospect_id) return;
        if (!generatedEmail[generated_for_prospect_id]) return;
        setConversationalHistories((prevConversationalHistories) => {
            if (!generated_for_prospect_id) return prevConversationalHistories;
            const original_email = generatedEmail[generated_for_prospect_id];
            const conversational_histories = prevConversationalHistories[generated_for_prospect_id] ?? [];
            const generated_emails = conversationalGeneratedEmails[generated_for_prospect_id] ?? {};
            if (!_canRequestConversationalHistory(original_email, conversational_histories, generated_emails)) return prevConversationalHistories;
            return {
                ...prevConversationalHistories,
                    [generated_for_prospect_id]: [...conversational_histories, conversational_edit]
            };
        });
    }, [getProspectIdForGeneration, generatedEmail, conversationalGeneratedEmails, _canRequestConversationalHistory]);

    const getPlatformUrl = useCallback(async () => {
        try {
            const url = await chrome.runtime.sendMessage(EXTENSION_ID, { type: MESSAGE_TYPES.EXTERNAL_TO_APP_GET_URL});
            return url;
        } catch (e) {
            return null;
        }
    }, []);

    const getVoices = useCallback((is_male: boolean) => {
        if (is_male) {
            return [BotVoiceType.MIDDLE_AGED_MAN, BotVoiceType.AUTHORITATIVE_MAN, BotVoiceType.CALM_MAN, BotVoiceType.YOUNG_MAN, BotVoiceType.OLD_MAN];
        } else {
            return [BotVoiceType.MIDDLE_AGED_WOMAN, BotVoiceType.AUTHORITATIVE_WOMAN, BotVoiceType.CALM_WOMAN, BotVoiceType.YOUNG_WOMAN, BotVoiceType.OLD_WOMAN];
        }
    }, []);

    const getVoice = useCallback((is_male: boolean) => {
        const voices = getVoices(is_male);
        return voices[Math.floor(Math.random() * voices.length)];
    }, [getVoices]);

    const createBot = useCallback(async () => {
        if (!getProspectIdForGeneration()) return;
        if (!email_value_props || email_value_props.length === 0) return;
        const url = await getPlatformUrl();
        const prospect_name = inputData.prospect_name;
        if (!prospect_name) return;
        if (!user?.user_id) return;
        const prospect_id = getProspectIdForGeneration();
        if(!prospect_id) return;
        try {
            setIsCreatingBot(true);
            setCurrentView(PageView.PRACTICE_BOT_VIEW);
            const botGeneration = await getServicesManager().generateBotFromProspectInformation(inputData, email_value_props[0]);
            if (botGeneration) {
                const bot = await getServicesManager().addBot({
                    'bot_id': uuid(),
                    'team_id': user.team_id,
                    'meta_description': botGeneration.meta_description,
                    'background_instruction': botGeneration.background,
                    'is_global': false,
                    'bot_language': BotLanguage.ENGLISH,
                    'bot_style': BotStyle.COLD_CALL,
                    'objections_instruction': botGeneration.objections,
                    'problem_instruction': botGeneration.problems,
                    'custom_instructions': botGeneration.custom_instructions,
                    'is_active': true,
                    'name': prospect_name.trim().split(' ')[0],
                    'updated_at': new Date().toISOString(),
                    'tags': [],
                    'user_id': user.user_id,
                    'xref_profile_url': url,
                    'xref_prospect_id': prospect_id,
                    'voice': getVoice(botGeneration.is_male)
                });
                if (bot) {
                    setProspectBots((prevProspectBots) => ({
                        ...prevProspectBots,
                            [prospect_id]: bot
                    }));
                }
            }
        } catch (error) {
            console.error('Error generating bot:', error);
        } finally {
            setIsCreatingBot(false);
        }
    }, [getProspectIdForGeneration, email_value_props, getPlatformUrl, inputData, user, getVoice]);

    const onBotRequest = useCallback(async () => {
        const generated_for_prospect_id = getProspectIdForGeneration();
        if (!generated_for_prospect_id) return;
        if (prospectBotsSearching[generated_for_prospect_id]) {
            setCurrentView(PageView.PRACTICE_BOT_VIEW);
            return;
        }
        if (!prospectBotsSearched[generated_for_prospect_id]) {
            setProspectBotsSearching((prev) => ({...prev, [generated_for_prospect_id]: true}));
            setCurrentView(PageView.PRACTICE_BOT_VIEW);
            const prospectBotsResponse = await getServicesManager().getProspectBots(generated_for_prospect_id);
            if (prospectBotsResponse && prospectBotsResponse.length > 0) {
                setProspectBots((prev) => ({...prev, [generated_for_prospect_id]: prospectBotsResponse[0]}));
                setProspectBotsSearching((prev) => ({...prev, [generated_for_prospect_id]: false}));
                setProspectBotsSearched((prev) => ({...prev, [generated_for_prospect_id]: true}));
                setCurrentView(PageView.PRACTICE_BOT_VIEW);
                return;
            } else {
                setProspectBotsSearching((prev) => ({...prev, [generated_for_prospect_id]: false}));
                setProspectBotsSearched((prev) => ({...prev, [generated_for_prospect_id]: true}));
            }
        }
        if (isCreatingBot || prospectBots[generated_for_prospect_id]) {
            setCurrentView(PageView.PRACTICE_BOT_VIEW);
            return;
        } else {
            createBot();
        }
    }, [getProspectIdForGeneration, prospectBotsSearching, prospectBotsSearched, isCreatingBot, prospectBots, createBot]);

    const handleDifficultyChange = useCallback((difficulty: CallDifficultyMode) => {
        setSelectedDifficulty(difficulty);
    }, []);

    const handleLanguageChange = useCallback((language: CallLanguage) => {
        setSelectedLanguage(language);
    }, []);

    const _getCurrentView = useCallback(() => {
        if (currentView === PageView.EMAIL_GENERATION_VIEW && getProspectIdForGeneration() === emailRequestedForProspectId) return PageView.EMAIL_GENERATION_VIEW;
        else if (currentView === PageView.PRACTICE_BOT_VIEW && getProspectIdForGeneration()) return PageView.PRACTICE_BOT_VIEW;
        return PageView.PROSPECT_DATA_VIEW;
    }, [currentView, getProspectIdForGeneration, emailRequestedForProspectId]);

    const renderNoDataDetected = useCallback(() => {
        return <div className="p-4 flex-grow flex gap-3 justify-center items-center text-center">
            <Typography className="" variant="largeParagraph">
                {getProspectIdForGeneration() ? 'Fetching information': 'No prospect detected'}
            </Typography>
        </div>;
    }, [getProspectIdForGeneration]);

    const _renderInnerBody = useCallback(() => {
        if (!_hasAnyData()) return renderNoDataDetected();
        const conversational_history = getProspectIdForGeneration() ? conversationalGeneratedEmails[getProspectIdForGeneration() ?? ''] ?? {} : {};
        const formatted_conversational_history: {[uuid: string]: string | null} = conversational_history ? Object.fromEntries(Object.entries(conversational_history).map(((v) => { return [v[0], v[1] ? v[1].gen_email : null]}))) : {};

        switch(_getCurrentView()) {
            case PageView.PROSPECT_DATA_VIEW: return <ProspectOverviewPage 
                onEmailRequest={() => {
                    setEmailRequestedForProspectId(getProspectIdForGeneration());
                    setCurrentView(PageView.EMAIL_GENERATION_VIEW);
                }}
                onSectionUpdate={(section) => setSelectedSection(section)}
                selected_section={selectedSection}
                data={inputData}
                phoneNumbers={inputData.numbers}
                onBotRequest={onBotRequest}
            />;
            case PageView.EMAIL_GENERATION_VIEW: return <ChatGPTGenerationPage
                conversational_history={getProspectIdForGeneration() ? conversationalHistories[getProspectIdForGeneration() ?? ''] ?? [] : []}
                conversational_history_emails={formatted_conversational_history}
                request_conversational_history_update={(conversational_edit: CustomEditInfo) => _onRequestConversationHistoryUpdate(conversational_edit)}
                can_request_conversational_history={getProspectIdForGeneration() ? _canRequestConversationalHistory(
                    generatedEmail[getProspectIdForGeneration() ?? ''],
                    conversationalHistories[getProspectIdForGeneration() ?? ''] || [],
                    conversationalGeneratedEmails[getProspectIdForGeneration() ?? ''] || {}
                ) : false}
                original_email={getProspectIdForGeneration() ? (generatedEmail[getProspectIdForGeneration() ?? '']?.gen_email || null) : null}
                onGoBack={() => setCurrentView(PageView.PROSPECT_DATA_VIEW)}
            />;
            case PageView.PRACTICE_BOT_VIEW: return (
                <PracticeBotView
                    bot={getProspectIdForGeneration() ? prospectBots[getProspectIdForGeneration() ?? ''] : null}
                    onBack={() => setCurrentView(PageView.PROSPECT_DATA_VIEW)}
                    isConstructing={isCreatingBot}
                    selectedDifficulty={selectedDifficulty}
                    onDifficultyChange={handleDifficultyChange}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={handleLanguageChange}
                    isSearching={getProspectIdForGeneration() ? prospectBotsSearching[getProspectIdForGeneration() ?? ''] : false}
                    prospectData={inputData}
                />
            );
        }
    }, [_hasAnyData, renderNoDataDetected, getProspectIdForGeneration, conversationalGeneratedEmails, _getCurrentView, selectedSection, inputData, onBotRequest, conversationalHistories, _onRequestConversationHistoryUpdate, _canRequestConversationalHistory, generatedEmail, isCreatingBot, selectedDifficulty, handleDifficultyChange, selectedLanguage, handleLanguageChange, prospectBotsSearching, prospectBots]);

    // Mount effect
    useEffect(() => {
        window.addEventListener('message', onWindowMessage);
        if (email_value_props === null) reloadEmailValueProps(dispatch);
        if (user === null) reloadUser(dispatch);

        return () => {
            window.removeEventListener('message', onWindowMessage);
        };
    }, [onWindowMessage, email_value_props, dispatch, user]);

    // Effect for prospect changes
    useEffect(() => {
        const currentProspectId = getProspectIdForGeneration();
        const previousProspectId = convertProspectIdForGeneration(latestProspectId);
        
        if (currentProspectId !== previousProspectId) {
            setCurrentView(PageView.PROSPECT_DATA_VIEW);
            setEmailRequestedForProspectId(null);
            
            // Clear all input data when prospect changes or clears
            if (!currentProspectId) {
                setInputData(getEmptyProspectData());
            }
        }
    }, [getProspectIdForGeneration, convertProspectIdForGeneration, latestProspectId]);

    // Effect for agent data changes - complex logic from old version
    useEffect(() => {
        const prevAgentData = prevAgentDataRef.current;
        
        for (const agent_type of Object.values(AGENT_TYPE)) {
            if (inputData.agent_data[agent_type].url &&
                ((
                    // URL Changed - triggers scraping when URL changes
                    inputData.agent_data[agent_type].url !== prevAgentData[agent_type].url && inputData.agent_data[agent_type].url
                ) || 
                (
                    // Status Changed - triggers scraping when status changes to REQUESTED_BY_USER
                    inputData.agent_data[agent_type].url === prevAgentData[agent_type].url && 
                    inputData.agent_data[agent_type].status === RequestState.REQUESTED_BY_USER && 
                    (agent_type !== AGENT_TYPE.LINKEDIN || inputData.agent_data[agent_type].scraped_data) &&
                    inputData.agent_data[agent_type].status !== prevAgentData[agent_type].status
                ) || 
                (
                    // Scraped Data Available - triggers scraping when scraped_data becomes available
                    inputData.agent_data[agent_type].url === prevAgentData[agent_type].url &&
                    !inputData.agent_data[agent_type].info && inputData.agent_data[agent_type].scraped_data &&
                    !prevAgentData[agent_type].scraped_data 
                )
                )) {
                const targetUrl = inputData.agent_data[agent_type].url;
                const scrapedData = inputData.agent_data[agent_type].scraped_data ?? null;
                const checkCache = agent_type === AGENT_TYPE.LINKEDIN ? scrapedData ? false : true : false;
                if (targetUrl) getScrapedData(targetUrl, scrapedData, checkCache, agent_type);
            }
        }
        
        // Update ref with current values for next comparison
        prevAgentDataRef.current = inputData.agent_data;
    }, [inputData.agent_data, getScrapedData]);

    // Effect for email generation
    useEffect(() => {
        if (getProspectIdForGeneration() && emailRequestedForProspectId && 
            emailRequestedForProspectId === getProspectIdForGeneration()) {
            generateEmail();
        }
    }, [getProspectIdForGeneration, emailRequestedForProspectId, generateEmail]);

    // Effect for conversational history changes
    useEffect(() => {
        const prospectId = getProspectIdForGeneration();
        if (prospectId && 
            conversationalHistories[prospectId] &&
            !_compareConversationHistories(conversationalHistories[prospectId] ?? [], prevConversationalHistoriesRef.current[prospectId] ?? []) && 
            generatedEmail[prospectId]?.gen_email) {
            
            // check if all prior conversations have either been generated or returned...
            const previous_conversations = prevConversationalHistoriesRef.current[prospectId] ?? [];
            const latest_prospect_id = prospectId;
            if (previous_conversations.some((v) => !(v.uuid in conversationalGeneratedEmails[latest_prospect_id ?? '']))) return; // check all previous conversations have been generated
            const emails_generated = [generatedEmail[latest_prospect_id ?? '']?.gen_email, ...previous_conversations.map((x) => conversationalGeneratedEmails[latest_prospect_id ?? ''][x.uuid]?.gen_email)].filter((v) => v);
            const last_email = emails_generated[emails_generated.length - 1] as string;
            const current_conversation = conversationalHistories[prospectId];
            generateRevisionEmail(last_email, current_conversation[current_conversation.length - 1]);
        }
        
        // Update the ref for next comparison
        prevConversationalHistoriesRef.current = conversationalHistories;
    }, [getProspectIdForGeneration, conversationalHistories, generatedEmail, conversationalGeneratedEmails, generateRevisionEmail, _compareConversationHistories]);

    return (
        <div className="w-screen h-screen">
            {_renderInnerBody()}
        </div>
    );
};

function getPriority(value_prop: EmailValueProp) {
    if (value_prop.user_id) return -1;
    if (value_prop.team_id) return 0;
    else return 1;
}